import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Bug, Sparkles, AlertTriangle, Plus, Copy, Check, Trash2, 
  Send, Archive, Clock, ChevronDown, ChevronRight, Camera,
  Monitor, Globe, RefreshCw
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Humano Pontuais - Sistema de Registro de Melhorias/Bugs
 * Seção colapsável para o AdminSaudeApp
 */
export const HumanoPontuais = ({ isExpanded, onToggle }) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    type: 'bug',
    description: '',
    page_url: window.location.href,
    technical_data: {}
  });

  // Fetch issues
  const fetchIssues = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/humano-pontuais/issues`, {
        withCredentials: true
      });
      setIssues(response.data.issues || []);
    } catch (error) {
      // Silent fail - admin only feature
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isExpanded) {
      fetchIssues();
    }
  }, [isExpanded]);

  // Collect technical data
  const collectTechnicalData = () => {
    return {
      userAgent: navigator.userAgent,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      localStorage_available: (() => {
        try {
          localStorage.setItem('test', 'test');
          localStorage.removeItem('test');
          return true;
        } catch (e) {
          return false;
        }
      })()
    };
  };

  // Create new issue
  const handleCreate = async () => {
    if (!formData.description.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }

    try {
      const technicalData = collectTechnicalData();
      
      await axios.post(`${API_URL}/api/humano-pontuais/issues`, {
        ...formData,
        page_url: window.location.href,
        technical_data: technicalData
      }, { withCredentials: true });
      
      toast.success('Registro criado com sucesso!');
      setShowForm(false);
      setFormData({ type: 'bug', description: '', page_url: '', technical_data: {} });
      fetchIssues();
    } catch (error) {
      toast.error('Erro ao criar registro');
    }
  };

  // Update status
  const handleUpdateStatus = async (issueId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/humano-pontuais/issues/${issueId}`, {
        status: newStatus
      }, { withCredentials: true });
      
      toast.success(`Status atualizado para: ${newStatus}`);
      fetchIssues();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  // Delete issue
  const handleDelete = async (issueId) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/humano-pontuais/issues/${issueId}`, {
        withCredentials: true
      });
      toast.success('Registro excluído');
      fetchIssues();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  // Copy single issue
  const handleCopy = async (issueId) => {
    try {
      const response = await axios.get(`${API_URL}/api/humano-pontuais/issues/${issueId}/copy`, {
        withCredentials: true
      });
      
      await navigator.clipboard.writeText(response.data.formatted_text);
      setCopiedId(issueId);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success('Copiado para a área de transferência!');
    } catch (error) {
      toast.error('Erro ao copiar');
    }
  };

  // Copy all pending
  const handleCopyAllPending = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/humano-pontuais/issues/copy-all-pending`, {}, {
        withCredentials: true
      });
      
      if (response.data.count === 0) {
        toast.info('Nenhum registro pendente para copiar');
        return;
      }
      
      await navigator.clipboard.writeText(response.data.formatted_text);
      toast.success(`${response.data.count} registro(s) copiado(s) e arquivado(s)!`);
      // Refresh list since items were auto-archived
      fetchIssues();
    } catch (error) {
      toast.error('Erro ao copiar');
    }
  };

  // Get type icon and color
  const getTypeConfig = (type) => {
    switch (type) {
      case 'bug':
        return { icon: <Bug className="w-4 h-4" />, color: 'text-red-400', bg: 'bg-red-500/20' };
      case 'melhoria':
        return { icon: <Sparkles className="w-4 h-4" />, color: 'text-amber-400', bg: 'bg-amber-500/20' };
      case 'problema':
        return { icon: <AlertTriangle className="w-4 h-4" />, color: 'text-orange-400', bg: 'bg-orange-500/20' };
      default:
        return { icon: <Bug className="w-4 h-4" />, color: 'text-stone-400', bg: 'bg-stone-500/20' };
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pendente':
        return <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded-full flex items-center gap-1"><Clock className="w-3 h-3" /> Pendente</span>;
      case 'enviado':
        return <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full flex items-center gap-1"><Send className="w-3 h-3" /> Enviado</span>;
      case 'resolvido':
        return <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full flex items-center gap-1"><Check className="w-3 h-3" /> Resolvido</span>;
      default:
        return null;
    }
  };

  // Separate issues by status
  const pendentes = issues.filter(i => i.status === 'pendente');
  const enviados = issues.filter(i => i.status === 'enviado');
  const resolvidos = issues.filter(i => i.status === 'resolvido');

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <div className="bg-stone-800/50 rounded-xl border border-stone-700/50 overflow-hidden">
      {/* Header colapsável */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-stone-800/70 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
            <Bug className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-medium text-white">Humano Pontuais</h3>
            <p className="text-sm text-stone-400">
              {pendentes.length > 0 
                ? `${pendentes.length} pendente(s) para enviar`
                : 'Nenhum registro pendente'
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pendentes.length > 0 && (
            <span className="px-2 py-1 text-xs font-bold bg-amber-500 text-stone-900 rounded-full">
              {pendentes.length}
            </span>
          )}
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-stone-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-stone-400" />
          )}
        </div>
      </button>

      {/* Conteúdo expandido */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-4">
          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setShowForm(!showForm)}
              className="bg-rose-500 hover:bg-rose-600 text-white"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Registro
            </Button>
            
            {pendentes.length > 0 && (
              <Button
                onClick={handleCopyAllPending}
                variant="outline"
                size="sm"
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar Pendentes ({pendentes.length})
              </Button>
            )}
            
            <Button
              onClick={fetchIssues}
              variant="ghost"
              size="sm"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Form */}
          {showForm && (
            <div className="bg-stone-900/50 rounded-lg p-4 border border-stone-700/50 space-y-4">
              <h4 className="font-medium text-white">Novo Registro</h4>
              
              {/* Type selection */}
              <div className="flex gap-2">
                {['bug', 'melhoria', 'problema'].map(type => {
                  const config = getTypeConfig(type);
                  return (
                    <button
                      key={type}
                      onClick={() => setFormData({ ...formData, type })}
                      className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                        formData.type === type 
                          ? `${config.bg} ${config.color} border border-current` 
                          : 'bg-stone-800 text-stone-400 hover:bg-stone-700'
                      }`}
                    >
                      {config.icon}
                      <span className="capitalize text-sm">{type}</span>
                    </button>
                  );
                })}
              </div>

              {/* Description */}
              <Textarea
                placeholder="Descreva o bug, melhoria ou problema..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="bg-stone-800 border-stone-700 text-white"
              />

              {/* Technical info preview */}
              <div className="text-xs text-stone-500 flex flex-wrap gap-4">
                <span className="flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  {window.location.pathname}
                </span>
                <span className="flex items-center gap-1">
                  <Monitor className="w-3 h-3" />
                  {window.innerWidth}x{window.innerHeight}
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button onClick={handleCreate} className="bg-rose-500 hover:bg-rose-600">
                  Salvar Registro
                </Button>
                <Button variant="ghost" onClick={() => setShowForm(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {/* Pending Issues */}
          {pendentes.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-amber-400 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Pendentes ({pendentes.length})
              </h4>
              {pendentes.map(issue => {
                const config = getTypeConfig(issue.type);
                return (
                  <div key={issue.issue_id} className="bg-stone-900/50 rounded-lg p-3 border border-amber-500/30">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`p-2 rounded-lg ${config.bg} ${config.color}`}>
                          {config.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-medium ${config.color} uppercase text-xs`}>{issue.type}</span>
                            {getStatusBadge(issue.status)}
                          </div>
                          <p className="text-sm text-white mt-1">{issue.description}</p>
                          <p className="text-xs text-stone-500 mt-1">{formatDate(issue.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(issue.issue_id)}
                          className="h-8 w-8 p-0"
                        >
                          {copiedId === issue.issue_id ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUpdateStatus(issue.issue_id, 'enviado')}
                          className="h-8 w-8 p-0"
                          title="Marcar como enviado"
                        >
                          <Send className="w-4 h-4 text-blue-400" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(issue.issue_id)}
                          className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Sent Issues (Archived) */}
          {enviados.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-blue-400 flex items-center gap-2">
                <Archive className="w-4 h-4" />
                Enviados ({enviados.length})
              </h4>
              {enviados.slice(0, 5).map(issue => {
                const config = getTypeConfig(issue.type);
                return (
                  <div key={issue.issue_id} className="bg-stone-900/30 rounded-lg p-3 border border-stone-700/50 opacity-75">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`${config.color}`}>{config.icon}</span>
                        <span className="text-sm text-stone-300 truncate max-w-xs">{issue.description}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-stone-500">{formatDate(issue.sent_at)}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUpdateStatus(issue.issue_id, 'resolvido')}
                          className="h-6 px-2 text-xs text-green-400 hover:text-green-300"
                        >
                          Resolvido
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {issues.length === 0 && !loading && (
            <div className="text-center py-8 text-stone-500">
              <Bug className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum registro ainda</p>
              <p className="text-sm">Clique em "Novo Registro" para começar</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HumanoPontuais;
